import { useState, useEffect } from 'react';
import { CreditCard, Check, Coins, TrendingUp, Sparkles, Mail, Infinity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContextSupabase';
import { supabase } from '../lib/supabase';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  pricePerCredit: number;
  discount?: string;
  popular?: boolean;
}

const CREDIT_PACKS: CreditPack[] = [
  {
    id: 'pack_500',
    name: 'Pack Grow',
    credits: 500,
    price: 449,
    pricePerCredit: 0.90,
  },
  {
    id: 'pack_2000',
    name: 'Pack Scale',
    credits: 2000,
    price: 1499,
    pricePerCredit: 0.75,
    popular: true,
  },
  {
    id: 'pack_6000',
    name: 'Pack Agency',
    credits: 6000,
    price: 3999,
    pricePerCredit: 0.66,
  },
];

const CREDIT_USAGE = [
  {
    icon: Sparkles,
    feature: 'Matching IA',
    credits: 2,
    description: 'Trouvez les créateurs parfaits pour votre marque',
  },
  {
    icon: TrendingUp,
    feature: 'Refresh Profil',
    credits: 1,
    description: 'Mettez à jour les données d\'un créateur',
  },
  {
    icon: Mail,
    feature: 'Débloquer Email',
    credits: 1,
    description: 'Révélez l\'email d\'un lead en prospection',
  },
];

export default function Credits() {
  const { currentUser, userProfile } = useAuth();
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [processingPack, setProcessingPack] = useState<string | null>(null);

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadCredits();
    }
  }, [userProfile?.agency_id]);

  const loadCredits = async () => {
    if (!userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsBalance(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (packId: string) => {
    if (!currentUser) return;

    try {
      setProcessingPack(packId);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ packId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de la création de la session de paiement');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Erreur lors de la création de la session de paiement. Veuillez réessayer.');
    } finally {
      setProcessingPack(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Coins className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Acheter des crédits
            </h1>
            <p className="text-white/40">
              Alimentez vos fonctionnalités IA avec des crédits
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-white/40 mb-1">Solde actuel</p>
            <p className="text-3xl font-bold text-white flex items-center gap-2">{creditsBalance >= 999999 ? <><Infinity className="w-8 h-8" /> crédits illimités</> : <>{creditsBalance} crédits</>}</p>
          </div>
          <div className="w-16 h-16 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Coins className="w-8 h-8 text-amber-400" />
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-white mb-4">Utilisation des crédits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {CREDIT_USAGE.map((usage) => (
            <div
              key={usage.feature}
              className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <usage.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white mb-1">{usage.feature}</h3>
                  <p className="text-sm text-white/40 mb-2">{usage.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-amber-400">{usage.credits}</span>
                    <span className="text-sm text-white/40">crédit{usage.credits > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-white mb-4">Packs de crédits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CREDIT_PACKS.map((pack) => (
            <div
              key={pack.id}
              className={`rounded-xl border bg-white/[0.04] p-6 relative ${
                pack.popular ? 'border-white/30' : 'border-white/[0.08]'
              }`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-white text-black px-3 py-1 rounded-full text-xs font-semibold">
                    Plus populaire
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">{pack.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">{pack.credits}</span>
                  <span className="text-lg text-white/40 ml-2">crédits</span>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-amber-400">{pack.price}€</span>
                </div>
                {pack.discount && (
                  <div className="mb-4">
                    <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs font-semibold">
                      {pack.discount}
                    </span>
                  </div>
                )}
                <p className="text-sm text-white/40">{pack.pricePerCredit.toFixed(2)}€ par crédit</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white/60">Crédits sans expiration</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white/60">Utilisables par toute l'équipe</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white/60">Paiement sécurisé via Stripe</span>
                </div>
              </div>

              <button
                onClick={() => handlePurchase(pack.id)}
                disabled={processingPack === pack.id}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  pack.popular
                    ? 'bg-white text-black hover:bg-white/90'
                    : 'bg-white/[0.08] text-white hover:bg-white/[0.14] border border-white/[0.08]'
                } ${processingPack === pack.id ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {processingPack === pack.id ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                    <span>Redirection...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Acheter</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.06] p-6">
        <h3 className="font-semibold text-white mb-2">Bon à savoir</h3>
        <ul className="space-y-2 text-sm text-white/50">
          <li>• Les crédits sont partagés au niveau de l'organisation</li>
          <li>• Aucune expiration : vos crédits restent disponibles indéfiniment</li>
          <li>• Paiement unique, pas d'abonnement</li>
          <li>• Toutes les fonctionnalités de base restent gratuites</li>
        </ul>
      </div>
    </div>
  );
}
